/**
 * Sends scoreCard details to CAL / FPTI.
 *
 * When we need samples we can capture these scoreCard details. By default
 * this is off. We enable this via UCP healthCheck config when we need those samples
 *
 */
import type { ScoreCardType } from './scoreCard';
import { THRESHOLD_FATAL } from './scoreCard';
import { getToken, getWeasleyAppConfig } from '@/lib/globals/globals';
import { isProd, isTest } from '../utils';

type EventLog = Weasley.Logger.EventLog;

const weasleyAppConfig = getWeasleyAppConfig();

// Allows collecting detailed sample reports to CAL and FPTI
// should be off normally to save logging volume. Enable for troubleshooting.
const sendSampleScoreCardsToCalAndFPTI =
  weasleyAppConfig.healthCheck?.SEND_SCORECARD_DETAILS_TO_CAL_FPTI ?? false;

export type SendScoreCardProps = Partial<{
  level: 'fatal' | 'success';
  reason: string;
  src: string;
  error: Error;
  event: string;
  eventSrc: string;
}>;

const getBrowserUrlPath = (): string => {
  try {
    const url = new URL(window?.location?.href);
    return url.pathname;
  } catch (err) {
    const { message, name } =
      err instanceof Error ? err : ({} as Partial<Error>);
    return message || name || 'hc_error_reading_location_href';
  }
};

/**
 * Sends the detailed Health Report (ScoreCard) to CAL/FPTI IF the UCP flag is enabled.
 * Even if not, will still log a report to browser via console.debug().
 * Report that can be stringified and logged to CAL/FPTI to give us qualitative
 * data points about the current health of this current user's session.
 */
export function sendDetailedScoreCard(
  props: SendScoreCardProps = {},
  scoreCard: ScoreCardType,
): void {
  const simpleFptiPayload = generateSimpleScoreCardPayloadForFPTI(
    props,
    scoreCard,
  );

  const domTree = scoreCard?.scoreReasons?.rootDomTree ?? '-1';

  const fptiPayload = Object.assign({}, simpleFptiPayload, {
    event_name: 'health_check_scorecard_detailed',
    hc_domTree: domTree,
  });

  const calPayload = generateScoreCardPayloadForCAL(props, scoreCard);

  const calDomTreePayload: EventLog = {
    // level: report.level === 'fatal' ? 'warn' : 'info',
    event: 'health_check_scorecard_domTree',
    level: props.level === 'fatal' ? 'warn' : 'info', // if error present, set to WARN in CAL, else use INFO
    payload: { domTree },
  };

  const payload = {
    // CAL
    events: [calPayload, calDomTreePayload],
    // FPTI
    tracking: [fptiPayload],
  };

  if (sendSampleScoreCardsToCalAndFPTI) {
    window?.logPayload?.(payload);
  }

  if (!isProd || isTest) {
    //eslint-disable-next-line no-console
    console.debug('##Health-check -> scoreCard:', scoreCard);
  }
}

/**
 * Sent to FPTI by default if healthcheck is ENABLED
 */
export function generateSimpleScoreCardPayloadForFPTI(
  props: SendScoreCardProps,
  scoreCard: Partial<ScoreCardType>,
): Record<string, string | number | undefined | null> {
  const { level, reason } = props;

  return {
    // We need window.__INITIAL_DATA__ check for scenario where the failure
    // is in index.html. If the runHealthCheck is called from any of react
    // components, then in live window.__INITIAL_DATA__ will be deleted so
    // we need a fallback option
    context_id: window?.__INITIAL_DATA__?.user?.ecToken || getToken(),
    event_name: 'health_check_scorecard_simple',
    experience: 'signuplite',
    feed_name: 'checkoutuinodeweb',
    hc_event: props.event ?? '-1',
    hc_eventSrc: scoreCard.eventSrc ?? '-1',
    hc_failed_assertions: scoreCard?.failedAssertions?.join(',') ?? '-1', // -1 = not found
    hc_level: level,
    hc_ready_in: window?.performance?.now() ?? '-1',
    hc_reason: reason,

    // TODO: consider removing these from "simple" report and keeping them in more detailed report
    // eslint-disable-next-line sort-keys
    hc_formFields: scoreCard?.scoreReasons?.formFields,
    hc_rootElDim: scoreCard?.scoreReasons?.rootElDim,
    hc_score: scoreCard.score,
    hc_src: props.src,
    hc_threshold: THRESHOLD_FATAL,
    hc_url_path: getBrowserUrlPath(),
    hc_visibleRootDepth: scoreCard?.scoreReasons?.visibleRootDepth,
    hc_visibleTextCount: scoreCard?.scoreReasons?.visibleTextCount,
  };
}

interface ScoreCardCalPayload {
  event: string;
  level: EventLog['level'];
  payload: Omit<SendScoreCardProps, 'error' | 'level'> & {
    level: EventLog['level'];
    hc_ready_in: EpochTimeStamp;
    hc_url_path: string;
    scoreReasons: Omit<
      ScoreCardType['scoreReasons'],
      'rootDomTree' | '_domReadyState'
    >;
  };
}

export function generateScoreCardPayloadForCAL(
  props: SendScoreCardProps,
  scoreCard: ScoreCardType,
): ScoreCardCalPayload {
  // report

  const { rootDomTree, ...simpleScoreReasons } = scoreCard?.scoreReasons ?? {};

  return {
    // level: report.level === 'fatal' ? 'warn' : 'info',
    event: 'health_check_scorecard',
    level: props?.level === 'fatal' ? 'warn' : 'info', // if error present, set to WARN in CAL, else use INFO
    payload: {
      ...scoreCard,
      event: props.event,
      eventSrc: scoreCard.eventSrc,
      hc_ready_in: window?.performance?.now() ?? '-1',
      hc_url_path: getBrowserUrlPath(),
      level: props.level || 'info',
      reason: props.reason,
      scoreReasons: simpleScoreReasons, // remove domTree to save limited cal record space
      src: props.src,
    },
  };
}
